<template>
  <div class="max-w-4xl mx-auto">
    <div class="text-center mb-8">
      <h1 class="text-2xl font-bold text-gray-900">Confirm plan & billing</h1>
      <p class="mt-2 text-lg text-gray-600">
        You are signing up for our Humaniz Professional plan which includes access to our full product as well as job
        board ads, meta ads, and monthly check-ins.
      </p>
    </div>

    <!-- Error States -->
    <div v-if="criticalError" class="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">Setup Error</h3>
          <div class="mt-2 text-sm text-red-700">
            {{ criticalError }}
          </div>
          <div class="mt-4">
            <button
              class="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
              @click="retrySetup"
            >
              Retry Setup
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="planData && loaded && !criticalError" class="mb-8 bg-gray-50 rounded-lg p-6">
      <div class="space-y-4">
        <div class="flex justify-between items-center">
          <span class="text-sm font-medium text-gray-600">How much you'll be charged today</span>
          <span class="text-lg font-semibold text-gray-900">$0</span>
        </div>

        <div class="flex justify-between items-center">
          <span class="text-sm font-medium text-gray-600">How much you'll be charged each month</span>
          <span class="text-lg font-semibold text-gray-900">{{ formatCurrency(planData.amount_in_cents) }}</span>
        </div>

        <div v-if="inviteData.initial_ad_spend > 0" class="flex justify-between items-center">
          <span class="text-sm font-medium text-gray-600">How much you'll be charged for ad spend each month</span>
          <span class="text-lg font-semibold text-gray-900">{{ formatCurrency(inviteData.initial_ad_spend) }}</span>
        </div>

        <div v-if="couponData" class="flex justify-between items-center text-green-600">
          <span class="text-sm font-medium">Discount Applied: {{ couponData.name }}</span>
          <span class="text-lg font-semibold">-{{ couponData.amount_off }}%</span>
        </div>
      </div>
    </div>

    <div v-if="errorMessage && !criticalError" class="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
      <div class="flex">
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">Payment Error</h3>
          <div class="mt-2 text-sm text-red-700">
            {{ errorMessage }}
          </div>
          <div class="mt-4">
            <button
              class="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
              @click="retryCheckout"
            >
              Retry Payment Setup
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!loaded && !criticalError" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      <span class="ml-3 text-gray-600">Setting up your billing information...</span>
    </div>

    <div v-else-if="clientSecret && !checkoutComplete && !criticalError" class="mb-8">
      <div ref="checkout" class="stripe-checkout"></div>
    </div>

    <div v-if="checkoutComplete" class="text-center py-12">
      <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
        <svg class="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
      </div>
      <h2 class="text-xl font-semibold text-gray-900 mb-2">Payment Successful!</h2>
      <p class="text-gray-600 mb-6">Your account has been set up successfully.</p>
      <TButton color="amber" label="Continue to Onboarding" @click="handleSuccess" />
    </div>

    <div v-if="loaded && !checkoutComplete && !criticalError" class="mt-6 text-center">
      <div class="inline-flex items-center text-sm text-gray-500">
        <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path
            fill-rule="evenodd"
            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
            clip-rule="evenodd"
          ></path>
        </svg>
        Secure payment powered by Stripe
      </div>
      <p class="mt-2 text-xs text-gray-400">
        Your payment information is encrypted and secure. We never store your card details.
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { loadStripe } from '@stripe/stripe-js';
import { stripePublishableKey } from '../../../utilities/pricingTable';
import { getPlatformFeePlan, getPlatformFeeCoupon } from '../../../api/billing';
import { useInviteDataStore } from '@/stores/inviteData.ts';

const inviteDataStore = useInviteDataStore();
const route = useRoute();
const emit = defineEmits(['go-forward']);

const loaded = ref(false);
const stripe = ref(null);
const checkoutElement = ref(null);
const clientSecret = ref(null);
const errorMessage = ref('');
const criticalError = ref('');
const checkoutComplete = ref(false);
const planData = ref(null);
const couponData = ref(null);
const inviteToken = ref(null);
const retryCount = ref(0);
const maxRetries = ref(3);
const checkout = ref(null);

const inviteData = computed(() => inviteDataStore.inviteData);

async function initializeBilling() {
  try {
    await loadInviteData();
    if (!stripe.value) {
      stripe.value = await loadStripe(stripePublishableKey);
      if (!stripe.value) {
        throw new Error('Failed to load Stripe. Please check your connection and try again.');
      }
    }
    await createCheckoutSession();
  } catch (error) {
    handleInitializationError(error);
  }
}

async function loadInviteData() {
  await inviteDataStore.fetchInviteData({ inviteToken: inviteToken.value });
  await Promise.all([loadPlanData(), loadCouponData()]);
}

async function loadPlanData() {
  const response = await getPlatformFeePlan(inviteData.value.stripe_plan_id);
  planData.value = response.record;
}

async function loadCouponData() {
  if (inviteData.value?.stripe_coupon_id) {
    const response = await getPlatformFeeCoupon(inviteData.value.stripe_coupon_id);
    couponData.value = response.record;
  }
}

async function createCheckoutSession() {
  const response = await fetch('/api/checkout_sessions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
    },
    body: JSON.stringify({
      invite_token: inviteToken.value,
    }),
  });
  const data = await response.json();

  if (!response.ok || data.status === 'error') {
    if (response.status === 401) {
      throw new Error('Authentication required. Please sign in and try again.');
    } else if (response.status === 404) {
      throw new Error('No billing setup found for your account. Please contact support.');
    } else if (response.status === 400 && data.message?.includes('invite')) {
      throw new Error('Invitation not in correct state for billing. Please contact support.');
    } else {
      throw new Error(data.message || `Billing setup failed (${response.status})`);
    }
  }

  if (!data.client_secret) {
    throw new Error('Invalid billing session created. Please try again.');
  }

  clientSecret.value = data.client_secret;
  loaded.value = true;
  await initializeCheckout();
}

async function initializeCheckout() {
  try {
    await nextTick();

    if (!checkout.value) {
      throw new Error('Payment form container not ready');
    }

    const embeddedCheckout = await stripe.value.initEmbeddedCheckout({
      clientSecret: clientSecret.value,
      onComplete: async () => {
        checkoutComplete.value = true;
      },
    });
    embeddedCheckout.mount(checkout.value);
    checkoutElement.value = embeddedCheckout;
  } catch (error) {
    throw new Error(`Failed to initialize payment form: ${error.message}`);
  }
}

function handleInitializationError(error) {
  retryCount.value += 1;
  if (error.message?.includes('Authentication required') || error.message?.includes('Team setup required')) {
    criticalError.value = error.message;
  } else if (retryCount.value < maxRetries.value) {
    errorMessage.value = `${error.message} (Attempt ${retryCount.value}/${maxRetries.value})`;
    loaded.value = true;
  } else {
    criticalError.value = `Setup failed after ${maxRetries.value} attempts: ${error.message}`;
  }
}

async function retrySetup() {
  criticalError.value = '';
  errorMessage.value = '';
  loaded.value = false;
  retryCount.value = 0;
  await initializeBilling();
}

async function retryCheckout() {
  errorMessage.value = '';
  loaded.value = false;

  try {
    await createCheckoutSession();
  } catch (error) {
    handleInitializationError(error);
  }
}

function handleSuccess() {
  emit('go-forward');
}

function formatCurrency(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

onMounted(async () => {
  inviteToken.value = route.params.invite_token;
  await initializeBilling();
});

onBeforeUnmount(() => {
  if (checkoutElement.value) {
    checkoutElement.value.unmount();
  }
});
</script>

<style scoped>
.stripe-checkout {
  min-height: 400px;
  width: 100%;
  position: relative;
  background-color: transparent;
}

/* Ensure the checkout element has proper spacing and styling */
.stripe-checkout :deep(iframe) {
  width: 100% !important;
  border: none;
  border-radius: 8px;
}

/* Loading state styling */
.stripe-checkout:empty::before {
  content: 'Loading payment form...';
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: #6b7280;
  font-size: 14px;
}
</style>
